/**
 * useUnsavedChangesGuard
 *
 * FT-449 — Reusable unsaved-changes guard hook.
 *
 * PURE LOGIC — no JSX, no React component import. The dialog rendering is
 * handled entirely by the caller (e.g. AddCrewView.js) using
 * UnsavedChangesDialog.jsx and the dialog props returned here. This keeps
 * the hook testable in isolation without any JSX parse issues.
 *
 * Usage:
 *   const {
 *     showLeavePrompt,
 *     dialogProps,   // spread onto <UnsavedChangesDialog {...dialogProps} />
 *   } = useUnsavedChangesGuard(isDirty);
 *
 *   - isDirty {boolean}  — caller supplies the dirty flag; hook observes it.
 *
 * What the hook does:
 *   1. While isDirty is true, attaches a 'beforeunload' listener so the browser
 *      shows its native "changes may not be saved" prompt on tab-close/refresh.
 *      The listener is removed when isDirty becomes false OR the component
 *      unmounts — no leaks, no lingering warning.
 *
 *   2. Exposes showLeavePrompt(onLeaveCallback):
 *      - If NOT dirty  -> calls onLeaveCallback() immediately (no dialog shown).
 *      - If dirty      -> opens the dialog (sets dialogProps.isOpen=true) so the
 *                         caller's rendered <UnsavedChangesDialog> becomes visible.
 *
 *   3. Returns dialogProps: { isOpen, onStay, onLeave } — the caller renders
 *      <UnsavedChangesDialog {...dialogProps} /> once in their JSX; it is a
 *      visual no-op when dialogProps.isOpen is false.
 *
 * Design invariants:
 *   - No false positives: caller owns isDirty; must not set it until a real change.
 *   - Successful save: caller resets isDirty to false before calling onBack — guard
 *     lets navigation proceed silently.
 *   - Completely form-agnostic; applicable to any component with a boolean isDirty.
 *   - This file contains zero JSX — fully testable by vitest without JSX transforms.
 */

import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * @param {boolean} isDirty  - True when the form has unsaved changes.
 * @returns {{
 *   showLeavePrompt: (onLeaveCallback: () => void) => void,
 *   dialogProps: { isOpen: boolean, onStay: () => void, onLeave: () => void },
 * }}
 */
export function useUnsavedChangesGuard(isDirty) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const pendingLeaveCallbackRef = useRef(null);

  // ── 1. beforeunload listener ──────────────────────────────────────────────
  // Added when dirty, removed immediately when clean or on unmount (no leak).
  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      // Deprecated but still required for some browser implementations
      // eslint-disable-next-line no-param-reassign
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  // ── 2. Show in-app leave prompt ──────────────────────────────────────────
  /**
   * Gate for every "exit" action (Cancel, back-button, etc.).
   * - Clean  -> executes onLeaveCallback immediately.
   * - Dirty  -> opens the dialog; executes onLeaveCallback only if the user
   *             confirms "Leave without saving".
   */
  const showLeavePrompt = useCallback(
    (onLeaveCallback) => {
      if (!isDirty) {
        if (typeof onLeaveCallback === 'function') {
          onLeaveCallback();
        }
        return;
      }
      pendingLeaveCallbackRef.current = onLeaveCallback;
      setDialogOpen(true);
    },
    [isDirty]
  );

  // ── 3. Dialog handlers ───────────────────────────────────────────────────
  const handleStay = useCallback(() => {
    pendingLeaveCallbackRef.current = null;
    setDialogOpen(false);
  }, []);

  const handleLeave = useCallback(() => {
    setDialogOpen(false);
    const cb = pendingLeaveCallbackRef.current;
    pendingLeaveCallbackRef.current = null;
    if (typeof cb === 'function') {
      cb();
    }
  }, []);

  // ── 4. Dialog props for the caller to spread ─────────────────────────────
  return {
    showLeavePrompt,
    dialogProps: {
      isOpen: dialogOpen,
      onStay: handleStay,
      onLeave: handleLeave,
    },
  };
}

export default useUnsavedChangesGuard;
